// Deep links from the SEO pages under public/scales/ and from shared URLs:
//   /?instrument=mandolin&key=G&structure=Pentatonic&tonality=Minor&mode=Dorian
// parseDeepLink() turns a query string into the state the app should open. It is pure, so the
// validation and fallback rules live in one place and can be tested without rendering the app.
import { VALID_INSTRUMENTS } from './instruments';
import { ALL_KEYS, MODE_NAMES, arpeggioTypesFor } from './theory';

const SCALE_STRUCTURES = ['Full Scale', 'Pentatonic', 'Modes', 'Arpeggios'];

/**
 * Per instrument:
 *  - accepts:        the `structure` values a link may carry (anything else is ignored).
 *  - map:            an accepted value the instrument has no view for -> what it opens instead.
 *                    null means "ignore it" (keep the default Full Scale view).
 *  - modeForcesModes: a valid `mode` also switches Structure to Modes (guitar, dobro, fiddle).
 *                    Mandolin and banjo only store the mode, as before.
 *  - arpeggios:      the instrument has an Arpeggios view with an arpeggio type to resolve.
 */
const RULES = {
  guitar:      { accepts: SCALE_STRUCTURES, map: {}, modeForcesModes: true, arpeggios: true },
  // Mandolin gained Arpeggios in Oct 2026 (Chop Chords / Double Stops are still hidden).
  mandolin:    { accepts: SCALE_STRUCTURES, map: {}, modeForcesModes: false, arpeggios: true },
  // Banjo and dobro have no Arpeggios; their nearest view is the chord tones.
  banjo:       { accepts: SCALE_STRUCTURES, map: { Arpeggios: 'Chords' }, modeForcesModes: false },
  dobro:       { accepts: SCALE_STRUCTURES, map: { Arpeggios: 'Chords' }, modeForcesModes: true },
  fiddle: {
    accepts: ['Full Scale', 'Pentatonic', 'Double Stops', 'Modes', 'Arpeggios'],
    map: {}, modeForcesModes: true, arpeggios: true,
  },
  // The bass has no Arpeggios view; a major / minor arpeggio is the triad's notes, so open Triad
  // (dominant 7th pages carry no tonality and get the major triad). It has no Modes view either;
  // those pages link with only a key, so they open the Scale view.
  uprightBass: {
    accepts: ['Full Scale', 'Pentatonic', 'Root & 5th', 'Triad', 'Chromatic', 'Arpeggios'],
    map: { Arpeggios: 'Triad' }, modeForcesModes: false,
  },
};

/**
 * @typedef {object} DeepLink
 * @property {string} instrument 'guitar' | 'mandolin' | 'banjo' | 'dobro' | 'fiddle' | 'uprightBass'
 * @property {string} key          One of ALL_KEYS (default 'G').
 * @property {'Major'|'Minor'} tonality   (default 'Major')
 * @property {string} structure    A Structure value the instrument's controls offer (default 'Full Scale').
 *                                 For dobro this is the tray value; the app maps it onto category + type.
 * @property {string} mode         One of MODE_NAMES (default 'Ionian'). Only shown when structure is Modes.
 * @property {string|null} arpeggioType  'Major' | 'Minor' for guitar, mandolin and fiddle (matches tonality), else null.
 */

/**
 * Resolve a query string to the view the app should open.
 *
 * Rules (unchanged from the original App.js effect): a missing `instrument` means guitar; an unknown
 * one means "do nothing" (returns null, the app keeps the instrument from the URL path); an invalid key,
 * tonality, structure or mode is ignored and the default stands. Matching is exact and case-sensitive
 * after URL decoding, so `Full%20Scale` works and `full scale` does not.
 *
 * @param {string} search Query string, with or without the leading '?'.
 * @returns {DeepLink|null} null when there is nothing to apply.
 */
export function parseDeepLink(search) {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const instrument = params.get('instrument') ?? 'guitar';
  if (!VALID_INSTRUMENTS.includes(instrument)) return null;
  const rules = RULES[instrument];

  const key = params.get('key');
  const tonality = params.get('tonality');
  const structure = params.get('structure');
  const mode = params.get('mode');

  const resolvedTonality = tonality === 'Major' || tonality === 'Minor' ? tonality : 'Major';
  const resolvedMode = mode && MODE_NAMES.includes(mode) ? mode : 'Ionian';

  let resolvedStructure = 'Full Scale';
  if (structure && rules.accepts.includes(structure)) {
    const mapped = structure in rules.map ? rules.map[structure] : structure;
    if (mapped) resolvedStructure = mapped;
  }
  if (rules.modeForcesModes && mode && MODE_NAMES.includes(mode)) resolvedStructure = 'Modes';

  return {
    instrument,
    key: key && ALL_KEYS.includes(key) ? key : 'G',
    tonality: resolvedTonality,
    structure: resolvedStructure,
    mode: resolvedMode,
    // The arpeggio type follows the tonality ('Minor' link -> minor arpeggio), so the title and the
    // fretboard agree. The app's default type is 'Major'.
    arpeggioType: rules.arpeggios ? resolvedTonality : null,
  };
}

/**
 * The view to open when the user switches instruments, so D major on the mandolin opens D major
 * on the guitar. It runs the current view through the same rules as a deep link, so a structure the
 * target has no view for lands where an SEO link would (Full Scale, or the nearest view). Chord
 * views travel as Arpeggios, which banjo / dobro read as Chords and the bass as Triad.
 *
 * @param {{key: string, tonality: string, structure: string, mode?: string, arpeggioType?: string|null}} view
 *        The instrument being left. `structure` is a Structure tray value (dobro: dobroStructureOf()).
 * @param {string} toInstrument
 * @returns {DeepLink|null}
 */
export function carryView({ key, tonality, structure, mode, arpeggioType }, toInstrument) {
  const carried = structure === 'Chords' || structure === 'Triad' ? 'Arpeggios' : structure;
  const params = new URLSearchParams({ instrument: toInstrument, key, tonality, structure: carried });
  if (structure === 'Modes' && mode) params.set('mode', mode);
  const link = parseDeepLink(params.toString());
  // Keep an extended arpeggio (Maj7, Min9, ...) when the target has it; the link alone only knows
  // the plain triad.
  if (link?.arpeggioType && arpeggioType && arpeggioTypesFor(link.tonality).all.includes(arpeggioType)) {
    link.arpeggioType = arpeggioType;
  }
  return link;
}
